<div class="fm-stories-page">

  <!-- ═══════════════════════════════════════════════════════════════════
       Action Cards — Record a Story / Schedule a Prompt
       ═══════════════════════════════════════════════════════════════════ -->
  <div class="row align-items-stretch">
    <div class="col-md-6">
      <div class="fm-card fm-primary-card fm-card-pattern-round h-100">
        <div class="row">
          <div class="col-xxl-11">
            <div class="d-flex flex-column">
              <div class="fm-icon-cl fm-icon-white-cl">
                <img class="fm-svg" src="images/icons/fm-video.svg" alt="Record a Story" />
              </div>
              <h3 class="fm-head-24 fm-card-title fm-head-sty2">Record a Story</h3>
              <p>Capture your memories in your own voice and turn moments into stories that last forever for generations.</p>
              <a routerLink="/app/stories/record" class="fm-common-tbtn">
                Record Your Story <img class="fm-svg" src="images/icons/fm-arrow-ar.svg" alt="Right Arrow" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="col-md-6 mt-4 mt-md-0">
      <div class="fm-card fm-secondary-card fm-card-pattern-round h-100">
        <div class="row">
          <div class="col-xxl-11">
            <div class="d-flex flex-column">
              <div class="fm-icon-cl fm-icon-white-cl">
                <img class="fm-svg" src="images/icons/fm-sprompt.svg" alt="Schedule a Prompt" />
              </div>
              <h3 class="fm-head-24 fm-card-title fm-head-sty2">Schedule a Prompt</h3>
              <p>Ask thoughtful, meaningful questions to spark memories and invite your loved ones to share their personal stories, experiences, and moments with you.</p>
              <a routerLink="/app/schedulepromt/create" class="fm-common-tbtn">
                Create a Prompt <img class="fm-svg" src="images/icons/fm-arrow-ar.svg" alt="Right Arrow" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- ═══════════════════════════════════════════════════════════════════
       Filter Section — Search | Filters Button | View Toggle
       ═══════════════════════════════════════════════════════════════════ -->
  <div class="row mt-4">
    <div class="col-12 mt-2 mt-xl-3">
      <div class="fm-white-column">

        <!-- ── Row 1: Search bar + Filters button + View toggle ── -->
        <div class="row gy-3 gx-3 gx-xl-4 align-items-center">

          <!-- Search bar -->
          <div class="col-12 col-sm">
            <div class="fm-searchbar pe-xl-2">
              <form (ngSubmit)="onSearch()">
                <div class="form-group">
                  <input
                    class="form-control"
                    type="text"
                    placeholder="Search stories by title, people"
                    aria-label="Search stories"
                    [(ngModel)]="customSearch"
                    name="storySearch"
                  />
                </div>
                @if (submittedSearch()) {
                  <!-- After search is submitted: button becomes a clear (×) button -->
                  <button
                    type="button"
                    class="fm-search-btn"
                    (click)="clearSearch()"
                    aria-label="Clear search"
                  >
                    &#x2715;
                  </button>
                } @else {
                  <button class="fm-search-btn" type="submit" aria-label="Search">
                    <img src="images/icons/fm-search-icon.svg" class="fm-svg" alt="Search Icon" />
                  </button>
                }
              </form>
            </div>
          </div>

          <!-- Filters button + View toggle -->
          <div class="col-sm-auto">
            <div class="d-flex align-items-center gap-3">

              <!-- Filters toggle button -->
              <button
                type="button"
                class="form-select fm-form-select"
                [class.fm-filters-open]="isFilterPanelOpen()"
                (click)="toggleFilterPanel()"
                aria-label="Toggle filters"
                [attr.aria-expanded]="isFilterPanelOpen()"
              >
                Filters
              
              </button>

              <!-- Grid / List view toggle -->
              <div class="fm-filter-btn-grp">
                <button
                  type="button"
                  class="fm-filter-cl-btn fm-filter-grid-btn"
                  [class.fm-fb-active]="viewMode() === 'grid'"
                  (click)="setViewMode('grid')"
                  aria-label="Grid view"
                >
                  <img src="images/icons/fm-column.svg" class="fm-svg" alt="Grid View" />
                </button>
                <button
                  type="button"
                  class="fm-filter-cl-btn fm-filter-list-btn"
                  [class.fm-fb-active]="viewMode() === 'list'"
                  (click)="setViewMode('list')"
                  aria-label="List view"
                >
                  <img src="images/icons/fm-list.svg" class="fm-svg" alt="List View" />
                </button>
              </div>

            </div>
          </div>
        </div>

        <!-- ── Row 2: Category filter tag bar ── -->
        <div class="row">
          <div class="col-12">
            <div class="fm-filter-tags fm-filter-tags-scroll mt-4" tabindex="0" aria-label="Filter stories by category">

              <!-- "All Stories" is always the first tab -->
              <button
                type="button"
                class="fm-filter-tag fm-filter-btn"
                [class.fm-ft-active]="activeCategoryId() === null"
                (click)="setCategory(null)"
              >
                All Stories
              </button>

              <!-- Categories from API -->
              @for (cat of categories(); track cat.categoryId) {
                <button
                  type="button"
                  class="fm-filter-tag fm-filter-btn"
                  [class.fm-ft-active]="activeCategoryId() === cat.categoryId"
                  (click)="setCategory(cat.categoryId)"
                >
                  {{ cat.categoryName }}
                  @if (activeCategoryId() === cat.categoryId) {
                    <span class="fm-tag-close" (click)="$event.stopPropagation(); setCategory(null)">&#x2715;</span>
                  }
                </button>
              }

            </div>
          </div>
        </div>

        <!-- ── Row 3: Expandable Filter Panel ── -->
        @if (isFilterPanelOpen()) {
          <div class="fm-filter-panel mt-3" role="region" aria-label="Advanced filters">

            <hr class="fm-filter-divider mt-0" />

            <!-- Filter by Family -->
            <div class="fm-filter-group">
              <p class="fm-black-cl fm-ft-title mt-xl-2">
                <img src="images/icons/fm-fg-icon.svg" alt="Filter by Family Icon" /> Filter by Family
              </p>
              <div class="fm-filter-tags fm-filter-tags-wrap">

                <button
                  type="button"
                  class="fm-filter-tag fm-filter-btn"
                  [class.fm-ft-active]="activeFamilySpaceId() === null"
                  (click)="onFamilySpaceChange('')"
                >
                  All Family
                </button>

                @for (family of familySpaces(); track family.familySpaceId) {
                  <button
                    type="button"
                    class="fm-filter-tag fm-filter-btn"
                    [class.fm-ft-active]="activeFamilySpaceId() === family.familySpaceId"
                    (click)="onFamilySpaceChange(family.familySpaceId)"
                  >
                    {{ family.familyName }}
                    @if (activeFamilySpaceId() === family.familySpaceId) {
                      <span class="fm-tag-close" (click)="$event.stopPropagation(); onFamilySpaceChange('')">&#x2715;</span>
                    }
                  </button>
                }

              </div>
            </div>

            <hr class="fm-filter-divider" />

            <!-- Filter by Tagged Person -->
            <div class="fm-filter-group">
              <p class="fm-black-cl fm-ft-title">
                <img src="images/icons/fm-fp-icon.svg" alt="Filter by Tagged Person Icon" /> Filter by Tagged Person
              </p>
              <div class="fm-filter-tags fm-filter-tags-wrap">

                <button
                  type="button"
                  class="fm-filter-tag fm-filter-btn"
                  [class.fm-ft-active]="activeTaggedPersonIds().length === 0"
                  (click)="activeTaggedPersonIds.set([]); pageNumber.set(1); loadStories()"
                >
                  All People
                </button>

                @for (member of familyMembers(); track member.familyMemberId) {
                  <button
                    type="button"
                    class="fm-filter-tag fm-filter-btn"
                    [class.fm-ft-active]="activeTaggedPersonIds().includes(member.familyMemberId)"
                    (click)="toggleTaggedPerson(member.familyMemberId)"
                  >
                    {{ member.fullName }}
                    @if (activeTaggedPersonIds().includes(member.familyMemberId)) {
                      <span class="fm-tag-close" (click)="$event.stopPropagation(); toggleTaggedPerson(member.familyMemberId)">&#x2715;</span>
                    }
                  </button>
                }

              </div>
            </div>

            <hr class="fm-filter-divider" />

            <!-- Filter by Creator -->
            <div class="fm-filter-group">
              <p class="fm-black-cl fm-ft-title">
                <img src="images/icons/fm-fp-icon.svg" alt="Filter by Creator Icon" /> Filter by Creator
              </p>
              <div class="fm-filter-tags fm-filter-tags-wrap">

                <button
                  type="button"
                  class="fm-filter-tag fm-filter-btn"
                  [class.fm-ft-active]="activeCreatorIds().length === 0"
                  (click)="activeCreatorIds.set([]); pageNumber.set(1); loadStories()"
                >
                  All Creator
                </button>

                @for (creator of creators(); track creator.userId) {
                  <button
                    type="button"
                    class="fm-filter-tag fm-filter-btn"
                    [class.fm-ft-active]="activeCreatorIds().includes(creator.userId)"
                    (click)="toggleCreator(creator.userId)"
                  >
                    {{ creator.fullName }}
                    @if (activeCreatorIds().includes(creator.userId)) {
                      <span class="fm-tag-close" (click)="$event.stopPropagation(); toggleCreator(creator.userId)">&#x2715;</span>
                    }
                  </button>
                }

              </div>
            </div>

            <hr class="fm-filter-divider" />

            <!-- Filter by Story Type -->
            <div class="fm-filter-group">
              <p class="fm-black-cl fm-ft-title">
                <img src="images/icons/fm-fp-icon.svg" alt="Filter by Story Type Icon" /> Filter by Story Type
              </p>
              <div class="fm-filter-tags fm-filter-tags-wrap">

                @for (opt of storyTypeOptions; track opt.value) {
                  <button
                    type="button"
                    class="fm-filter-tag fm-filter-btn"
                    [class.fm-ft-active]="(opt.value === '' && activeStoryType() === null) || (opt.value !== '' && activeStoryType() === +opt.value)"
                    (click)="onStoryTypeChange(opt.value)"
                  >
                    {{ opt.label }}
                    @if (opt.value !== '' && activeStoryType() === +opt.value) {
                      <span class="fm-tag-close" (click)="$event.stopPropagation(); onStoryTypeChange('')">&#x2715;</span>
                    }
                  </button>
                }

              </div>
            </div>

            <!-- Applied Filters summary row -->
            @if (hasActiveFilters) {
              <hr class="fm-filter-divider" />
              <div class="fm-filter-group">
                <div class="fm-filter-title">
                  <p class="fm-black-cl fm-ft-title mb-0">
                    <img src="images/icons/fm-af-icon.svg" alt="Applied Filters Icon" /> Applied Filters
                  </p>
                  <p class="fm-black-cl fm-ft-title mb-0" style="cursor:pointer;" (click)="clearAllFilters()">
                    Clear All Filters <img src="images/icons/fm-bl-x.svg" alt="Applied Filters Icon" />
                  </p>
                </div>
                <div class="fm-filter-tags fm-filter-tags-wrap mt-3">

                  @if (activeCategoryId() !== null) {
                    <button type="button" class="fm-filter-tag fm-filter-btn fm-ft-active" (click)="setCategory(null)">
                      {{ getActiveCategoryName() }}
                      <span class="fm-tag-close">&#x2715;</span>
                    </button>
                  }

                  @if (activeStoryType() !== null) {
                    <button type="button" class="fm-filter-tag fm-filter-btn fm-ft-active" (click)="onStoryTypeChange('')">
                      {{ getStoryTypeName() }}
                      <span class="fm-tag-close">&#x2715;</span>
                    </button>
                  }

                  @if (activeFamilySpaceId() !== null) {
                    <button type="button" class="fm-filter-tag fm-filter-btn fm-ft-active" (click)="onFamilySpaceChange('')">
                      {{ getActiveFamilyName() }}
                      <span class="fm-tag-close">&#x2715;</span>
                    </button>
                  }

                  @for (id of activeTaggedPersonIds(); track id) {
                    <button type="button" class="fm-filter-tag fm-filter-btn fm-ft-active" (click)="toggleTaggedPerson(id)">
                      {{ familyMembers().find(m => m.familyMemberId === id)?.fullName }}
                      <span class="fm-tag-close">&#x2715;</span>
                    </button>
                  }

                  @for (id of activeCreatorIds(); track id) {
                    <button type="button" class="fm-filter-tag fm-filter-btn fm-ft-active" (click)="toggleCreator(id)">
                      {{ creators().find(c => c.userId === id)?.fullName }}
                      <span class="fm-tag-close">&#x2715;</span>
                    </button>
                  }

                </div>
              </div>
            }

          </div>
        }

      </div>
    </div>
  </div>

  <!-- ═══════════════════════════════════════════════════════════════════
       Stories Area — hidden while initializing (global loader is shown);
       mutually exclusive states once data is ready:
         1. Error    → retry message
         2. Empty (no active filters) → "start recording" prompt
         3. Empty (filters active)    → "no results found"
         4. Success  → grid / list of story cards + pagination
       ═══════════════════════════════════════════════════════════════════ -->

  @if (!isInitializing()) {

  <!-- ── 1. API / network error ── -->
  @if (hasError()) {
    <div class="fm-stories-empty-state">
      <div class="fm-white-column text-center mt-4">
        <h4 class="fm-head-24 fm-head-sty2">Something went wrong</h4>
        <p class="mb-3">We couldn't load your stories. Please check your connection and try again.</p>
      </div>
    </div>
  }

  <!-- ── 2. Empty — no stories in the system at all (with or without filters) ── -->
  @else if (stories().length === 0 && !hasStoriesInSystem()) {
    <div class="fm-stories-empty-state">
      <div class="fm-white-column text-center mt-4">
        <h4 class="fm-head-24 fm-head-sty2">Your story is waiting to be told!</h4>
        <p class="mb-3">
          No memories have been added yet — start recording and preserve the moments
          that matter forever.
        </p>
       
      </div>
    </div>
  }

  <!-- ── 3. Empty — stories exist but active filters/search returned no results ── -->
  @else if (stories().length === 0 && hasStoriesInSystem()) {
    <div class="fm-stories-empty-state">
      <div class="fm-white-column text-center mt-4">
        <h4 class="fm-head-24 fm-head-sty2">No stories found</h4>
        <p class="mb-0">Try a different search term or filter to see available stories.</p>
      </div>
    </div>
  }

  <!-- ── 4. Success — story cards ── -->
  @else {
    <div
      class="row mt-2 g-3 fm-stories-grid align-items-stretch"
      [class.fm-list-view]="viewMode() === 'list'"
    >
      @for (story of stories(); track story.storyId) {
        <div
          [class]="viewMode() === 'grid'
            ? 'col-sm-6 col-lg-4 mt-4 fm-story-filter-item'
            : 'col-12 mt-3 fm-story-filter-item'"
        >
          <div
            class="fm-stories-blog fm-match-height h-100"
            [class.fm-uploading]="story.vimeoUploadStatus === null"
          >

            <!-- Cover image / video thumbnail -->
            <div class="fm-stories-cover">
              @if (story.vimeoUploadStatus === null) {
                <!-- Processing placeholder — shown instead of default thumbnail while uploading -->
                <div class="fm-upload-thumbnail-placeholder" [attr.aria-label]="story.title">
                  <img src="images/fm-upload-lg.svg" class="fm-svg fm-upload-thumb-icon" alt="Uploading" />
                </div>
              } @else {
                <a [routerLink]="['/app/stories', story.storyId]" class="fm-stories-cover-link">
                  <img
                    class="fm-image"
                    [src]="story.vimeoThumbnailUrl || 'images/stories/stories-post-1.png'"
                    [alt]="story.title"
                    onerror="this.src='images/stories/stories-post-1.png'"
                  />
                </a>
              }

              @if (story.vimeoUploadStatus === null) {
                <!-- Upload-in-progress overlay — hides play button and dims thumbnail -->
                <div class="fm-upload-overlay" role="status" aria-label="Story upload in progress">
                  <div class="fm-upload-spinner"></div>
                  <span class="fm-upload-label">Upload in progress…</span>
                </div>
              } @else {
                <!-- Duration badge + play button — only shown when story is ready -->
                <span class="fm-tag">{{ formatDuration(story.duration) }}</span>
                <button
                  type="button"
                  class="fm-stories-pp"
                  [attr.aria-label]="'Play ' + story.title"
                >
                  <img src="images/icons/fm-play.svg" alt="Play" class="fm-svg" />
                </button>
              }
            </div>

            <!-- Story metadata -->
            <div class="fm-stories-content">
              <div class="fm-st-promt-tag">
                <span class="fm-st-ti">{{ story.categoryName || '—' }}</span>
                @if (story.isFromPrompt) {
                  <span class="fm-st-ti fm-green">
                    <img src="images/icons/fm-magic.svg" alt="Prompt icon" /> Prompt
                  </span>
                }
              </div>

              <!-- Title: link when ready, plain text while uploading -->
              <h4 class="fm-head-20 fm-head-sty2">
                @if (story.vimeoUploadStatus === null) {
                  <span class="fm-story-title-uploading">{{ story.title }}</span>
                } @else {
                  <a [routerLink]="['/app/stories', story.storyId]">{{ story.title }}</a>
                }
              </h4>

              <div class="fm-user-profile">
                <div class="fm-userprofile">
                  <div class="fm-left">
                    <div class="fm-userprofile-image">
                      @if (story.authorPhotoURL) {
                        <img
                          class="fm-profile"
                          [src]="story.authorPhotoURL"
                          [alt]="story.storyAuthorName || 'Story author'"
                          onerror="this.style.display='none'; this.nextElementSibling.style.display='flex'"
                        />
                        <span class="fm-profile fm-profile-initials" style="display:none;">
                          {{ getAuthorInitials(story.storyAuthorName) }}
                        </span>
                      } @else {
                        <span class="fm-profile fm-profile-initials">
                          {{ getAuthorInitials(story.storyAuthorName) }}
                        </span>
                      }
                    </div>
                    <span class="fm-profile-content">
                      <span class="fm-profilename">
                        @if (story.vimeoUploadStatus === null) {
                          <span>{{ story.storyAuthorName || 'Story Author' }}</span>
                        } @else {
                          <a [routerLink]="['/app/stories', story.storyId]">{{ story.storyAuthorName || 'Story Author' }}</a>
                        }
                      </span>
                      <span class="fm-dateinfo">{{ getRelativeTime(story.createdOn) }}</span>
                    </span>
                  </div>

                  <!-- Views & likes — hidden while upload is in progress -->
                  @if (story.vimeoUploadStatus !== null) {
                    <ul class="fm-activity fm-rp-info">
                      <li>
                        <span>
                          <img class="fm-svg" src="images/icons/fm-eye.svg" alt="Views" />
                          {{ story.viewCount }}
                        </span>
                      </li>
                      <li class="fm-reaction-badge-wrap">
                        <span class="fm-reaction-default">
                          <span class="fm-reaction-emoji">&#x2764;&#xFE0F;</span>
                          {{ story.totalReactionCount }}
                        </span>
                        <div class="fm-reaction-tooltip">
                          @if (story.heartCount > 0) {
                            <div class="fm-reaction-tooltip-row">
                              <span class="fm-rt-emoji">&#x2764;&#xFE0F;</span>
                              <span class="fm-rt-count">{{ story.heartCount }}</span>
                            </div>
                          }
                          @if (story.likeCount > 0) {
                            <div class="fm-reaction-tooltip-row">
                              <span class="fm-rt-emoji">&#x1F44D;</span>
                              <span class="fm-rt-count">{{ story.likeCount }}</span>
                            </div>
                          }
                          @if (story.celebrateCount > 0) {
                            <div class="fm-reaction-tooltip-row">
                              <span class="fm-rt-emoji">&#x1F389;</span>
                              <span class="fm-rt-count">{{ story.celebrateCount }}</span>
                            </div>
                          }
                          @if (story.supportCount > 0) {
                            <div class="fm-reaction-tooltip-row">
                              <span class="fm-rt-emoji">&#x1F91D;</span>
                              <span class="fm-rt-count">{{ story.supportCount }}</span>
                            </div>
                          }
                          @if (story.totalReactionCount > 0) {
                            <div class="fm-reaction-tooltip-total">
                              {{ story.totalReactionCount }} reaction{{ story.totalReactionCount === 1 ? '' : 's' }}
                            </div>
                          } @else {
                            <div class="fm-reaction-tooltip-empty">No reactions yet</div>
                          }
                        </div>
                      </li>
                    </ul>
                  }
                </div>

                @if (story.familyName) {
                  <hr class="my-0" />
                  <p class="fm-black-cl fm-icon-content mb-0 mt-1">
                    <img src="images/icons/fm-family-group.svg" class="fm-family-icon" alt="Family" />
                    @if (story.vimeoUploadStatus !== null) {
                      <a [routerLink]="['/app/stories', story.storyId]" class="fm-story-nav-link">{{ story.familyName }}</a>
                    } @else {
                      <span>{{ story.familyName }}</span>
                    }
                  </p>
                }
              </div>
            </div>

          </div>
        </div>
      }
    </div>

    <!-- ── Infinite-scroll: load-more spinner ── -->
    @if (isLoadingMore()) {
      <div class="fm-load-more-spinner text-center py-4">
        <div class="spinner-border" role="status" style="width:1.75rem;height:1.75rem;border-width:3px;">
          <span class="visually-hidden">Loading more stories…</span>
        </div>
      </div>
    }

    <!-- Sentinel — IntersectionObserver watches this to trigger the next page -->
    @if (hasMoreStories()) {
      <div id="storiesScrollSentinel" class="fm-scroll-sentinel"></div>
    }

  }

  } <!-- end @if (!isInitializing()) -->

</div>